import api from './api';
import { 
  PAYMENT_TYPES, 
  PAYMENT_METHODS, 
  PAYMENT_STATUSES, 
  CURRENCIES,
  STRIPE_METHODS,
  CASH_METHODS,
  SUCCESS_STATUSES,
  PENDING_STATUSES
} from '../constants/paymentConstants';

class PaymentService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Récupère le dashboard financier du locataire (totaux et métriques)
   */
  async getRenterDashboard(period = {}) {
    try {
      console.log('🔄 [PaymentService] Fetching renter dashboard...');
      
      const params = new URLSearchParams();
      if (period.start && period.end) {
        params.append('period', `${period.start}_${period.end}`);
      }
      
      const queryString = params.toString();
      const endpoint = `/financial/renter/dashboard${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(endpoint);
      
      if (response.data && response.data.success) {
        const dashboardData = response.data.data;
        
        console.log('✅ [PaymentService] Renter dashboard loaded:', {
          totalSpent: dashboardData.totals?.total_spent || 0,
          paymentsCount: dashboardData.payments?.length || 0
        });
        
        // Récupérer les paiements et les dépôts depuis la source de vérité
        const payments = dashboardData.payments || [];
        const deposits = dashboardData.deposits || [];
        
        // Identifier les réservations uniques basées sur les paiements
        const uniqueReservationIds = this.getUniqueReservationIds(payments);
        const reservationsWithPayments = this.getReservationsWithPayments(payments);
        
        // Statistiques réellement basées sur les paiements
        const completedPayments = payments.filter(p => SUCCESS_STATUSES.includes(p.status));
        const pendingPayments = payments.filter(p => PENDING_STATUSES.includes(p.status));
        const totalsByReservation = this.calculateTotalsByReservation(payments);
        
        console.log('🔍 [DEBUG] Source unique de vérité - Paiements:', {
          totalPayments: payments.length,
          completedPayments: completedPayments.length,
          pendingPayments: pendingPayments.length,
          uniqueReservations: uniqueReservationIds.length,
          totalsByReservation: Object.keys(totalsByReservation).length
        });
        
        return {
          success: true,
          data: {
            // Données brutes
            payments: payments,
            deposits: deposits,
            period: dashboardData.period || {},
            user_id: dashboardData.user_id,
            view_type: dashboardData.view_type || 'renter',
            
            // Statistiques calculées uniquement à partir des paiements
            stats: {
              // Réservations
              uniqueReservationsCount: uniqueReservationIds.length,
              reservationsWithCompleted: this.getReservationsWithCompletedPayments(payments).length,
              reservationsWithPending: this.getReservationsWithPendingPayments(payments).length,
              
              // Paiements
              totalPayments: payments.length,
              completedPayments: completedPayments.length,
              pendingPayments: pendingPayments.length,
              
              // Montants 
              totalPaid: this.calculateTotalAmount(completedPayments),
              totalPending: this.calculateTotalAmount(pendingPayments),
              
              // Méthodes de paiement
              cashTotal: this.calculateTotalByCashMethod(completedPayments),
              onlineTotal: this.calculateTotalByOnlineMethod(completedPayments),
              
              // Ventilation par type
              rentalPaid: this.calculateTotalByType(completedPayments, PAYMENT_TYPES.RENTAL),
              commissionPaid: this.calculateTotalByType(completedPayments, PAYMENT_TYPES.COMMISSION),
              taxesPaid: this.calculateTotalByType(completedPayments, PAYMENT_TYPES.TAX)
            },
            
            // Statistiques détaillées par devise (toujours utiles)
            currencyBreakdown: this.calculateCurrencyBreakdown(payments),
            
            // Distinction Cash vs Online (toujours utile)
            paymentMethodBreakdown: this.calculatePaymentMethodBreakdown(payments),
            
            // Dépôts de garantie détaillés
            depositsBreakdown: this.calculateDepositsBreakdown(deposits),
            
            // Dates importantes
            firstPaymentDate: this.getFirstPaymentDate(payments),
            lastPaymentDate: this.getLastPaymentDate(payments),
            
            // Statistiques de transactions améliorées
            transactionStats: this.calculateTransactionStats(payments),
            
            // Liste des réservations avec leurs paiements associés
            reservationsWithPayments: reservationsWithPayments
          }
        };
      }
      
      throw new Error('Invalid response format from backend');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to fetch renter dashboard:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  /**
   * Récupère les paiements d'un locataire avec distinction des devises et méthodes
   */
  async getRenterPayments(filters = {}) {
    try {
      console.log('🔄 [PaymentService] Fetching renter payments...');
      
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);
      if (filters.method) params.append('method', filters.method);
      if (filters.currency) params.append('currency', filters.currency);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);

      const queryString = params.toString();
      // Utiliser l'endpoint financial correct
      const endpoint = `/financial/renter/payment-history${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(endpoint);
      
      // Gérer la structure de réponse du backend
      if (response.data && response.data.success) {
        const responseData = response.data.data;
        
        console.log('✅ [PaymentService] Renter payments loaded:', {
          count: responseData.payments?.length || 0,
          total: responseData.totals?.total_spent || 0
        });
        
        // Retourner la structure attendue par le frontend
        return {
          data: responseData.payments || [],
          deposits: responseData.deposits || [],
          totals: responseData.totals || {},
          pagination: {
            total: responseData.payments?.length || 0,
            page: filters.page || 1,
            limit: filters.limit || 20
          },
          currencies: this.groupByCurrency(responseData.payments || []),
          paymentMethods: this.groupByMethod(responseData.payments || [])
        };
      }
      
      throw new Error('Invalid response format from backend');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to fetch renter payments:', error);
      throw error;
    }
  }

  /**
   * Récupère l'historique des paiements avec pagination infinie (groupé par réservation)
   */
  async getRenterPaymentHistory(filters = {}) {
    try {
      console.log('🔄 [PaymentService] Fetching renter payment history...');
      
      const params = new URLSearchParams();
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.method) params.append('method', filters.method);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const queryString = params.toString();
      const endpoint = `/financial/renter/payment-history${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(endpoint);
      
      if (response.data && response.data.success) {
        const responseData = response.data.data;
        
        console.log('✅ [PaymentService] Renter payment history loaded:', {
          reservations: responseData.history?.length || 0,
          page: responseData.pagination.current_page,
          hasNext: responseData.pagination.has_next_page
        });
        
        return {
          success: true,
          history: responseData.history || [],
          pagination: responseData.pagination,
          renter_id: responseData.renter_id
        };
      }
      
      throw new Error('Invalid response format from backend');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to fetch renter payment history:', error);
      throw error;
    }
  }

  /**
   * Récupère l'historique des revenus d'un owner avec pagination infinie et filtres avancés
   * NOUVEAU SERVICE OWNER - PERSPECTIVE PROPRIÉTAIRE avec filtres
   */
  async getOwnerRevenueHistory(filters = {}) {
    try {
      console.log('🔄 [PaymentService] Fetching owner revenue history with filters:', filters);
      
      const params = new URLSearchParams();
      
      // Pagination
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      
      // Filtres de base (compatibilité)
      if (filters.status) params.append('status', filters.status);
      if (filters.currency) params.append('currency', filters.currency);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      // Nouveaux filtres avancés
      if (filters.paymentMethod) {
        params.append('payment_method', filters.paymentMethod);
        console.log('✅ [PaymentService] Added payment method filter:', filters.paymentMethod);
      }
      if (filters.reservationStatus) {
        params.append('reservation_status', filters.reservationStatus);
        console.log('✅ [PaymentService] Added reservation status filter:', filters.reservationStatus);
      }
      
      // Gestion des plages de dates (priorité aux nouveaux filtres)
      if (filters.dateRange) {
        if (typeof filters.dateRange === 'string') {
          // Période prédéfinie (today, week, month, year)
          params.append('date_period', filters.dateRange);
          console.log('✅ [PaymentService] Added predefined date period:', filters.dateRange);
        } else if (typeof filters.dateRange === 'object' && filters.dateRange !== null) {
          // Plage personnalisée
          if (filters.dateRange.start) {
            params.append('date_start', filters.dateRange.start);
            console.log('✅ [PaymentService] Added custom start date:', filters.dateRange.start);
          }
          if (filters.dateRange.end) {
            params.append('date_end', filters.dateRange.end);
            console.log('✅ [PaymentService] Added custom end date:', filters.dateRange.end);
          }
        }
      }

      const queryString = params.toString();
      const endpoint = `/financial/owner/revenue-history${queryString ? `?${queryString}` : ''}`;
      
      console.log('🌐 [PaymentService] Calling endpoint:', endpoint);
      console.log('🔍 [PaymentService] Query parameters:', Object.fromEntries(params.entries()));
      
      const response = await api.get(endpoint);
      
      if (response.data && response.data.success) {
        const responseData = response.data.data;
        
        console.log('✅ [PaymentService] Owner revenue history loaded:', {
          reservations: responseData.history?.length || 0,
          page: responseData.pagination.current_page,
          hasNext: responseData.pagination.has_next_page
        });
        
        return {
          success: true,
          data: responseData
        };
      }
      
      throw new Error('Invalid response format from backend');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to fetch owner revenue history:', error);
      throw error;
    }
  }

  async getOwnerReservationReconciliation(filters = {}) {
    try {
      const params = new URLSearchParams();

      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.currency) params.append('currency', filters.currency);
      if (filters.paymentMethod) params.append('payment_method', filters.paymentMethod);
      if (filters.reservationStatus) params.append('reservation_status', filters.reservationStatus);
      if (filters.dateStart) params.append('date_start', filters.dateStart);
      if (filters.dateEnd) params.append('date_end', filters.dateEnd);

      const queryString = params.toString();
      const endpoint = `/financial/owner/reservation-reconciliation${queryString ? `?${queryString}` : ''}`;
      const response = await api.get(endpoint);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      }

      throw new Error('Invalid response format from backend');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to fetch owner reservation reconciliation:', error);
      throw error;
    }
  }

  /**
   * Récupère le dashboard financier d'un owner
   * NOUVEAU SERVICE OWNER - PERSPECTIVE PROPRIÉTAIRE
   */
  async getOwnerFinancialDashboard(filters = {}) {
    try {
      console.log('🔄 [PaymentService] Fetching owner financial dashboard...');
      
      const params = new URLSearchParams();
      if (filters.period_start) params.append('period_start', filters.period_start);
      if (filters.period_end) params.append('period_end', filters.period_end);
      if (filters.currency) params.append('currency', filters.currency);

      const queryString = params.toString();
      const endpoint = `/financial/owner/dashboard${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(endpoint);
      
      if (response.data && response.data.success) {
        const responseData = response.data.data;
        
        console.log('✅ [PaymentService] Owner financial dashboard loaded:', {
          totalRevenue: responseData.total_revenue || 0,
          listingsCount: responseData.listings_count || 0
        });
        
        return {
          success: true,
          data: responseData
        };
      }
      
      throw new Error('Invalid response format from backend');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to fetch owner financial dashboard:', error);
      throw error;
    }
  }

  /**
   * Récupère l'historique des dépôts d'un owner avec pagination infinie
   * Compatible avec DepositsHistoryInfinite pour le contexte propriétaire
   */
  async getOwnerDepositsHistory(filters = {}) {
    try {
      console.log('🔄 [PaymentService] Fetching owner deposits history...');
      
      const params = new URLSearchParams();
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.type) params.append('type', filters.type || 'deposit');
      if (filters.status) params.append('status', filters.status);
      if (filters.method) params.append('method', filters.method);
      if (filters.payment_status) params.append('payment_status', filters.payment_status); // Nouveau filtre
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.currency) params.append('currency', filters.currency);
      
      // Spécifique aux dépôts owner
      if (filters.include_deposits) params.append('include_deposits', filters.include_deposits);
      if (filters.deposits_only) params.append('deposits_only', filters.deposits_only);
      if (filters.component) params.append('component', filters.component);

      const queryString = params.toString();
      const endpoint = `/financial/owner/deposits-history${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(endpoint);
      
      if (response.data && response.data.success) {
        const responseData = response.data.data;
        
        console.log('✅ [PaymentService] Owner deposits history loaded:', {
          deposits: responseData.history?.length || 0,
          page: responseData.pagination.current_page,
          hasNext: responseData.pagination.has_next_page
        });
        
        return {
          success: true,
          history: responseData.history || [],
          pagination: responseData.pagination,
          owner_id: responseData.owner_id
        };
      }
      
      throw new Error('Invalid response format from backend');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to fetch owner deposits history:', error);
      throw error;
    }
  }

  /**
   * Récupère les revenus d'un propriétaire
   */
  async getOwnerPayments(filters = {}) {
    const cacheKey = `owner_payments_${JSON.stringify(filters)}`;
    
    if (this.cache.has(cacheKey)) {
      console.log('🎯 [PaymentService] Returning cached owner payments');
      return this.cache.get(cacheKey);
    }

    try {
      console.log('📊 [PaymentService] Fetching owner payments from /financial/owner/dashboard');
      
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.currency) params.append('currency', filters.currency);
      if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
      if (filters.status) params.append('status', filters.status);

      const response = await api.get(`/financial/owner/dashboard?${params.toString()}`);
      
      if (!response || !response.data) {
        console.warn('⚠️ [PaymentService] Invalid response format for owner payments');
        return { success: false, data: [], totalAmount: 0 };
      }

      // Structure attendue du backend pour les propriétaires
      const result = {
        success: true,
        data: response.data.payments || [],
        totalAmount: response.data.totalRevenue || 0,
        totalCommissions: response.data.totalCommissions || 0,
        totalTaxes: response.data.totalTaxes || 0,
        totalPaid: response.data.totalPaid || 0,
        totalPending: response.data.totalPending || 0,
        totalRefunded: response.data.totalRefunded || 0,
        statistics: response.data.statistics || {},
        groupedByMonth: response.data.groupedByMonth || {},
        groupedByProperty: response.data.groupedByProperty || {}
      };

      this.cache.set(cacheKey, result);
      console.log('✅ [PaymentService] Owner payments retrieved:', result);
      
      return result;
    } catch (error) {
      console.error('❌ [PaymentService] Error fetching owner payments:', error);
      return { 
        success: false, 
        error: error.message,
        data: [],
        totalAmount: 0 
      };
    }
  }

  /**
   * Récupère le dashboard financier d'un propriétaire
   */
  async getOwnerDashboard(ownerId, filters = {}) {
    const cacheKey = `owner_dashboard_${ownerId}_${JSON.stringify(filters)}`;
    
    if (this.cache.has(cacheKey)) {
      console.log('🎯 [PaymentService] Returning cached owner dashboard');
      return this.cache.get(cacheKey);
    }

    try {
      console.log('📊 [PaymentService] Fetching owner dashboard from /financial/owner/dashboard');
      
      const params = new URLSearchParams();
      if (ownerId) params.append('ownerId', ownerId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.currency) params.append('currency', filters.currency);
      if (filters.propertyId) params.append('propertyId', filters.propertyId);

      const response = await api.get(`/financial/owner/dashboard?${params.toString()}`);
      
      if (!response || !response.data) {
        console.warn('⚠️ [PaymentService] Invalid response format for owner dashboard');
        return { success: false, data: null };
      }

      // Structure attendue du backend pour le dashboard propriétaire
      const result = {
        success: true,
        data: {
          overview: {
            totalRevenue: response.data.totalRevenue || 0,
            totalCommissions: response.data.totalCommissions || 0,
            totalTaxes: response.data.totalTaxes || 0,
            netRevenue: response.data.netRevenue || 0,
            totalPayouts: response.data.totalPayouts || 0,
            pendingPayouts: response.data.pendingPayouts || 0
          },
          payments: response.data.payments || [],
          payouts: response.data.payouts || [],
          statistics: response.data.statistics || {},
          groupedByMonth: response.data.groupedByMonth || {},
          groupedByProperty: response.data.groupedByProperty || {},
          groupedByCurrency: response.data.groupedByCurrency || {},
          groupedByMethod: response.data.groupedByMethod || {}
        }
      };

      this.cache.set(cacheKey, result);
      console.log('✅ [PaymentService] Owner dashboard retrieved:', result);
      
      return result;
    } catch (error) {
      console.error('❌ [PaymentService] Error fetching owner dashboard:', error);
      return { 
        success: false, 
        error: error.message,
        data: null 
      };
    }
  }

  /**
   * Récupère les versements d'un propriétaire
   */
  async getOwnerPayouts(filters = {}) {
    try {
      console.log('🔄 [PaymentService] Fetching owner payouts...');
      
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.currency) params.append('currency', filters.currency);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);

      const queryString = params.toString();
      const endpoint = `/payouts/owner${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(endpoint);
      
      if (response.data) {
        console.log('✅ [PaymentService] Owner payouts loaded:', {
          count: response.data.length || 0,
          total: response.total || 0
        });
        
        return {
          data: response.data,
          total: response.total || response.data.length,
          page: response.page || 1,
          limit: response.limit || 20
        };
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to fetch owner payouts:', error);
      throw error;
    }
  }

  /**
   * Récupère les détails d'un paiement
   */
  async getPaymentDetails(paymentId) {
    try {
      console.log('🔄 [PaymentService] Fetching payment details:', paymentId);
      
      const response = await api.get(`/payments/${paymentId}`);
      
      if (response.data) {
        console.log('✅ [PaymentService] Payment details loaded');
        return response.data;
      }
      
      throw new Error('Payment not found');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to fetch payment details:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques financières d'un locataire (utilise le dashboard)
   */
  async getRenterFinancialStats(period = 'all') {
    try {
      console.log('🔄 [PaymentService] Fetching renter financial stats...');
      
      const cacheKey = `renter_stats_${period}`;
      const cached = this.cache.get(cacheKey);
      
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        console.log('📋 [PaymentService] Using cached renter stats');
        return cached.data;
      }
      
      // Utiliser le dashboard renter au lieu d'un endpoint stats séparé
      const dashboardData = await this.getRenterDashboard(
        period !== 'all' ? { start: period + '-01', end: period + '-31' } : {}
      );
      
      if (dashboardData.totals) {
        console.log('✅ [PaymentService] Renter financial stats loaded');
        
        const statsData = {
          // Utiliser les totaux calculés par le backend
          totalPaid: dashboardData.totals.total_spent || 0,
          rentalPaid: dashboardData.totals.rental_paid || 0,
          commissionsPaid: dashboardData.totals.commissions_paid || 0,
          taxesPaid: dashboardData.totals.taxes_paid || 0,
          depositsHeld: dashboardData.totals.deposits_held || 0,
          depositsRefunded: dashboardData.totals.deposits_refunded || 0,
          totalReservations: dashboardData.payments?.length || 0,
          pendingPayments: dashboardData.payments?.filter(p => 
            PENDING_STATUSES.includes(p.status)
          ).length || 0,
          completedPayments: dashboardData.payments?.filter(p => 
            SUCCESS_STATUSES.includes(p.status)
          ).length || 0,
          // Calculs par méthode de paiement
          stripePayments: dashboardData.payments?.filter(p => 
            STRIPE_METHODS.includes(p.method)
          ).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0) || 0,
          cashPayments: dashboardData.payments?.filter(p => 
            CASH_METHODS.includes(p.method)
          ).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0) || 0,
          lastPaymentDate: dashboardData.payments?.[0]?.transaction_date || null
        };
        
        this.cache.set(cacheKey, {
          data: statsData,
          timestamp: Date.now()
        });
        
        return statsData;
      }
      
      throw new Error('Invalid dashboard data format');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to fetch renter financial stats:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques financières d'un propriétaire
   */
  async getOwnerFinancialStats(period = 'all', currency = null) {
    try {
      console.log('🔄 [PaymentService] Fetching owner financial stats...');
      
      const cacheKey = `owner_stats_${period}_${currency || 'all'}`;
      const cached = this.cache.get(cacheKey);
      
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        console.log('📋 [PaymentService] Using cached owner stats');
        return cached.data;
      }
      
      const params = new URLSearchParams({ period });
      if (currency) params.append('currency', currency);
      
      const response = await api.get(`/payments/owner/stats?${params.toString()}`);
      
      if (response.data) {
        console.log('✅ [PaymentService] Owner financial stats loaded');
        
        const statsData = {
          totalEarnings: response.data.total_earnings || 0,
          totalPayouts: response.data.total_payouts || 0,
          pendingPayouts: response.data.pending_payouts || 0,
          completedPayouts: response.data.completed_payouts || 0,
          pendingEarnings: response.data.pending_earnings || 0,
          averageEarning: response.data.average_earning || 0,
          lastPayoutDate: response.data.last_payout_date || null,
          currency: response.data.currency || 'EUR'
        };
        
        this.cache.set(cacheKey, {
          data: statsData,
          timestamp: Date.now()
        });
        
        return statsData;
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to fetch owner financial stats:', error);
      throw error;
    }
  }

  /**
   * Télécharge un reçu de paiement
   */
  async downloadReceipt(paymentId) {
    try {
      console.log('🔄 [PaymentService] Downloading receipt for payment:', paymentId);
      
      const response = await api.get(`/payments/${paymentId}/receipt`, {
        responseType: 'blob'
      });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log('✅ [PaymentService] Receipt downloaded successfully');
      return true;
    } catch (error) {
      console.error('❌ [PaymentService] Failed to download receipt:', error);
      throw error;
    }
  }

  /**
   * Initie un paiement
   */
  async initiatePayment(paymentData) {
    try {
      console.log('🔄 [PaymentService] Initiating payment...');
      
      const response = await api.post('/payments/initiate', paymentData);
      
      if (response.data) {
        console.log('✅ [PaymentService] Payment initiated successfully');
        this.clearCache(); // Vider le cache après modification
        return response.data;
      }
      
      throw new Error('Failed to initiate payment');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to initiate payment:', error);
      throw error;
    }
  }

  /**
   * Confirme un paiement
   */
  async confirmPayment(paymentId, confirmationData = {}) {
    try {
      console.log('🔄 [PaymentService] Confirming payment:', paymentId);
      
      const response = await api.post(`/payments/${paymentId}/confirm`, confirmationData);
      
      if (response.data) {
        console.log('✅ [PaymentService] Payment confirmed successfully');
        this.clearCache(); // Vider le cache après modification
        return response.data;
      }
      
      throw new Error('Failed to confirm payment');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to confirm payment:', error);
      throw error;
    }
  }

  /**
   * Demande un remboursement
   */
  async requestRefund(paymentId, refundData) {
    try {
      console.log('🔄 [PaymentService] Requesting refund for payment:', paymentId);
      
      const response = await api.post(`/payments/${paymentId}/refund`, refundData);
      
      if (response.data) {
        console.log('✅ [PaymentService] Refund requested successfully');
        this.clearCache(); // Vider le cache après modification
        return response.data;
      }
      
      throw new Error('Failed to request refund');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to request refund:', error);
      throw error;
    }
  }

  /**
   * Récupère les moyens de paiement disponibles
   */
  async getPaymentMethods() {
    try {
      console.log('🔄 [PaymentService] Fetching payment methods...');
      
      const cacheKey = 'payment_methods';
      const cached = this.cache.get(cacheKey);
      
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        console.log('📋 [PaymentService] Using cached payment methods');
        return cached.data;
      }
      
      const response = await api.get('/payments/methods');
      
      if (response.data) {
        console.log('✅ [PaymentService] Payment methods loaded');
        
        this.cache.set(cacheKey, {
          data: response.data,
          timestamp: Date.now()
        });
        
        return response.data;
      }
      
      throw new Error('Failed to fetch payment methods');
    } catch (error) {
      console.error('❌ [PaymentService] Failed to fetch payment methods:', error);
      throw error;
    }
  }

  /**
   * Vide le cache
   */
  clearCache() {
    console.log('🗑️ [PaymentService] Clearing cache');
    this.cache.clear();
  }

  /**
   * Calcule la répartition par devise avec détails
   */
  calculateCurrencyBreakdown(payments) {
    console.log('🔍 [DEBUG] calculateCurrencyBreakdown input:', {
      paymentsCount: payments.length,
      samplePayments: payments.slice(0, 3).map(p => ({
        amount: p.amount,
        currency: p.currency,
        method: p.method || p.payment_method,
        type: p.type || p.payment_type
      }))
    });

    const breakdown = {};
    
    Object.values(CURRENCIES).forEach(currency => {
      breakdown[currency] = {
        currency,
        totalAmount: 0,
        transactions: 0,
        payments: [],
        byType: {},
        byMethod: { cash: 0, online: 0 },
        byStatus: {},
        avgTransaction: 0,
        firstDate: null,
        lastDate: null
      };
    });

    payments.forEach(payment => {
      const currency = payment.currency || 'EUR';
      const amount = parseFloat(payment.amount || payment.total_amount || payment.paid_amount) || 0;
      const type = payment.type || payment.payment_type || payment.transaction_type || 'unknown';
      const method = payment.method || payment.payment_method || payment.paymentMethod || payment.gateway || 'unknown';
      const status = payment.status || payment.payment_status || payment.transaction_status || 'unknown';
      const date = payment.created_at || payment.transaction_date || payment.date || payment.payment_date || payment.created_date;

      // DEBUG: Log structure de chaque paiement
      if (payments.indexOf(payment) < 3) {
        console.log(`🔍 [DEBUG] Payment ${payments.indexOf(payment)}:`, {
          currency, amount, type, method, status, date,
          rawPayment: payment
        });
      }

      if (!breakdown[currency]) {
        breakdown[currency] = {
          currency,
          totalAmount: 0,
          transactions: 0,
          payments: [],
          byType: {},
          byMethod: { cash: 0, online: 0 },
          byStatus: {},
          avgTransaction: 0,
          firstDate: null,
          lastDate: null
        };
      }

      breakdown[currency].totalAmount += amount;
      breakdown[currency].transactions++;
      breakdown[currency].payments.push(payment);

      // Par type
      if (!breakdown[currency].byType[type]) {
        breakdown[currency].byType[type] = { amount: 0, count: 0 };
      }
      breakdown[currency].byType[type].amount += amount;
      breakdown[currency].byType[type].count++;

      // Par méthode (Cash vs Online)
      if (CASH_METHODS.includes(method)) {
        breakdown[currency].byMethod.cash += amount;
      } else if (STRIPE_METHODS.includes(method)) {
        breakdown[currency].byMethod.online += amount;
      }

      // Par statut
      if (!breakdown[currency].byStatus[status]) {
        breakdown[currency].byStatus[status] = { amount: 0, count: 0 };
      }
      breakdown[currency].byStatus[status].amount += amount;
      breakdown[currency].byStatus[status].count++;

      // Dates
      if (!breakdown[currency].firstDate || new Date(date) < new Date(breakdown[currency].firstDate)) {
        breakdown[currency].firstDate = date;
      }
      if (!breakdown[currency].lastDate || new Date(date) > new Date(breakdown[currency].lastDate)) {
        breakdown[currency].lastDate = date;
      }
    });

    // Calcul des moyennes
    Object.keys(breakdown).forEach(currency => {
      if (breakdown[currency].transactions > 0) {
        breakdown[currency].avgTransaction = breakdown[currency].totalAmount / breakdown[currency].transactions;
      }
    });

    return breakdown;
  }

  /**
   * Calcule la répartition par méthode de paiement
   */
  calculatePaymentMethodBreakdown(payments) {
    const breakdown = {
      cash: {
        totalAmount: 0,
        transactions: 0,
        payments: [],
        methods: CASH_METHODS,
        byCurrency: {},
        byType: {},
        avgTransaction: 0
      },
      online: {
        totalAmount: 0,
        transactions: 0,
        payments: [],
        methods: STRIPE_METHODS,
        byCurrency: {},
        byType: {},
        avgTransaction: 0
      },
      other: {
        totalAmount: 0,
        transactions: 0,
        payments: [],
        methods: [],
        byCurrency: {},
        byType: {},
        avgTransaction: 0
      }
    };

    payments.forEach(payment => {
      const amount = parseFloat(payment.amount || payment.total_amount || payment.paid_amount) || 0;
      const method = payment.method || payment.payment_method || payment.paymentMethod || payment.gateway || 'unknown';
      const currency = payment.currency || 'EUR';
      const type = payment.type || payment.payment_type || payment.transaction_type || 'unknown';

      let category = 'other';
      if (CASH_METHODS.includes(method)) {
        category = 'cash';
      } else if (STRIPE_METHODS.includes(method)) {
        category = 'online';
      }

      breakdown[category].totalAmount += amount;
      breakdown[category].transactions++;
      breakdown[category].payments.push(payment);

      // Par devise
      if (!breakdown[category].byCurrency[currency]) {
        breakdown[category].byCurrency[currency] = { amount: 0, count: 0 };
      }
      breakdown[category].byCurrency[currency].amount += amount;
      breakdown[category].byCurrency[currency].count++;

      // Par type
      if (!breakdown[category].byType[type]) {
        breakdown[category].byType[type] = { amount: 0, count: 0 };
      }
      breakdown[category].byType[type].amount += amount;
      breakdown[category].byType[type].count++;
    });

    // Calcul des moyennes
    Object.keys(breakdown).forEach(category => {
      if (breakdown[category].transactions > 0) {
        breakdown[category].avgTransaction = breakdown[category].totalAmount / breakdown[category].transactions;
      }
    });

    return breakdown;
  }

  /**
   * Calcule la répartition des dépôts de garantie
   */
  calculateDepositsBreakdown(deposits) {
    console.log('🔍 [DEBUG] calculateDepositsBreakdown input:', {
      depositsCount: deposits.length,
      sampleDeposits: deposits.slice(0, 3).map(d => ({
        amount: d.amount,
        currency: d.currency,
        status: d.status,
        created_at: d.created_at
      }))
    });

    const breakdown = {
      total: {
        amount: 0,
        count: 0,
        held: 0,
        released: 0,
        refunded: 0
      },
      byCurrency: {},
      byStatus: {},
      byProperty: {},
      avgDeposit: 0,
      oldestHeld: null,
      newestHeld: null
    };

    deposits.forEach(deposit => {
      const amount = parseFloat(deposit.amount) || 0;
      const currency = deposit.currency || 'EUR';
      const status = deposit.status;
      const propertyId = deposit.property_id;
      const date = deposit.created_at;

      breakdown.total.amount += amount;
      breakdown.total.count++;

      if (status === 'held' || status === 'blocked') {
        breakdown.total.held += amount;
      } else if (status === 'released') {
        breakdown.total.released += amount;
      } else if (status === 'refunded') {
        breakdown.total.refunded += amount;
      }

      // Par devise
      if (!breakdown.byCurrency[currency]) {
        breakdown.byCurrency[currency] = { amount: 0, count: 0, held: 0, released: 0 };
      }
      breakdown.byCurrency[currency].amount += amount;
      breakdown.byCurrency[currency].count++;
      if (status === 'held' || status === 'blocked') {
        breakdown.byCurrency[currency].held += amount;
      }

      // Par statut
      if (!breakdown.byStatus[status]) {
        breakdown.byStatus[status] = { amount: 0, count: 0 };
      }
      breakdown.byStatus[status].amount += amount;
      breakdown.byStatus[status].count++;

      // Par propriété
      if (propertyId) {
        if (!breakdown.byProperty[propertyId]) {
          breakdown.byProperty[propertyId] = { amount: 0, count: 0 };
        }
        breakdown.byProperty[propertyId].amount += amount;
        breakdown.byProperty[propertyId].count++;
      }

      // Dates pour dépôts bloqués
      if (status === 'held' || status === 'blocked') {
        if (!breakdown.oldestHeld || new Date(date) < new Date(breakdown.oldestHeld)) {
          breakdown.oldestHeld = date;
        }
        if (!breakdown.newestHeld || new Date(date) > new Date(breakdown.newestHeld)) {
          breakdown.newestHeld = date;
        }
      }
    });

    if (breakdown.total.count > 0) {
      breakdown.avgDeposit = breakdown.total.amount / breakdown.total.count;
    }

    return breakdown;
  }

  /**
   * Calcule les statistiques de transactions
   */
  calculateTransactionStats(payments) {
    const stats = {
      totalTransactions: payments.length,
      successfulTransactions: 0,
      pendingTransactions: 0,
      failedTransactions: 0,
      // ❌ Supprimé: avgTransactionAmount global (invalide avec plusieurs devises)
      largestTransaction: { amount: 0, date: null, currency: 'EUR' },
      smallestTransaction: { amount: Infinity, date: null, currency: 'EUR' },
      transactionsByMonth: {},
      transactionsByDay: {},
      // ✅ Nouveau: Stats par devise
      byCurrency: {}
    };

    payments.forEach(payment => {
      const amount = parseFloat(payment.amount || payment.total_amount || payment.paid_amount) || 0;
      const status = payment.status || payment.payment_status || payment.transaction_status || 'unknown';
      const date = payment.created_at || payment.transaction_date || payment.date || payment.payment_date || payment.created_date;
      const currency = payment.currency || 'EUR';

      // ✅ Nouveau: Initialiser les stats par devise
      if (!stats.byCurrency[currency]) {
        stats.byCurrency[currency] = {
          totalAmount: 0,
          count: 0,
          avgAmount: 0
        };
      }
      
      // ✅ Nouveau: Calculer par devise au lieu de global
      stats.byCurrency[currency].totalAmount += amount;
      stats.byCurrency[currency].count++;

      // Comptage par statut
      if (SUCCESS_STATUSES.includes(status)) {
        stats.successfulTransactions++;
      } else if (PENDING_STATUSES.includes(status)) {
        stats.pendingTransactions++;
      } else {
        stats.failedTransactions++;
      }

      // Plus grande transaction
      if (amount > stats.largestTransaction.amount) {
        stats.largestTransaction = { amount, date, currency };
      }

      // Plus petite transaction
      if (amount < stats.smallestTransaction.amount && amount > 0) {
        stats.smallestTransaction = { amount, date, currency };
      }

      // Par mois
      if (date) {
        const month = new Date(date).toISOString().slice(0, 7); // YYYY-MM
        if (!stats.transactionsByMonth[month]) {
          stats.transactionsByMonth[month] = { count: 0, byCurrency: {} };
        }
        if (!stats.transactionsByMonth[month].byCurrency[currency]) {
          stats.transactionsByMonth[month].byCurrency[currency] = { count: 0, amount: 0 };
        }
        stats.transactionsByMonth[month].count++;
        stats.transactionsByMonth[month].byCurrency[currency].count++;
        stats.transactionsByMonth[month].byCurrency[currency].amount += amount;

        // Par jour
        const day = new Date(date).toISOString().slice(0, 10); // YYYY-MM-DD
        if (!stats.transactionsByDay[day]) {
          stats.transactionsByDay[day] = { count: 0, byCurrency: {} };
        }
        if (!stats.transactionsByDay[day].byCurrency[currency]) {
          stats.transactionsByDay[day].byCurrency[currency] = { count: 0, amount: 0 };
        }
        stats.transactionsByDay[day].count++;
        stats.transactionsByDay[day].byCurrency[currency].count++;
        stats.transactionsByDay[day].byCurrency[currency].amount += amount;
      }
    });

    // ✅ Calculer les moyennes par devise
    Object.keys(stats.byCurrency).forEach(currency => {
      if (stats.byCurrency[currency].count > 0) {
        stats.byCurrency[currency].avgAmount = stats.byCurrency[currency].totalAmount / stats.byCurrency[currency].count;
      }
    });

    if (stats.smallestTransaction.amount === Infinity) {
      stats.smallestTransaction = { amount: 0, date: null, currency: 'EUR' };
    }

    return stats;
  }

  /**
   * Récupère la date du premier paiement
   */
  getFirstPaymentDate(payments) {
    if (payments.length === 0) return null;
    
    return payments.reduce((earliest, payment) => {
      const date = payment.created_at || payment.transaction_date || payment.date || payment.payment_date || payment.created_date;
      return !earliest || (date && new Date(date) < new Date(earliest)) ? date : earliest;
    }, null);
  }

  /**
   * Récupère la date du dernier paiement
   */
  getLastPaymentDate(payments) {
    if (payments.length === 0) return null;
    
    return payments.reduce((latest, payment) => {
      const date = payment.created_at || payment.transaction_date || payment.date || payment.payment_date || payment.created_date;
      return !latest || (date && new Date(date) > new Date(latest)) ? date : latest;
    }, null);
  }

  /**
   * Groupe les paiements par devise
   */
  groupByCurrency(payments) {
    const grouped = {};
    
    Object.values(CURRENCIES).forEach(currency => {
      grouped[currency] = {
        currency,
        totalAmount: 0,
        payments: [],
        count: 0
      };
    });

    payments.forEach(payment => {
      const currency = payment.currency || 'EUR';
      const amount = parseFloat(payment.amount || payment.total_amount || payment.paid_amount) || 0;
      
      if (!grouped[currency]) {
        grouped[currency] = {
          currency,
          totalAmount: 0,
          payments: [],
          count: 0
        };
      }
      
      grouped[currency].totalAmount += amount;
      grouped[currency].payments.push(payment);
      grouped[currency].count++;
    });

    return grouped;
  }

  /**
   * Groupe les paiements par méthode (Stripe vs Cash)
   */
  groupByMethod(payments) {
    const grouped = {
      stripe: {
        methods: STRIPE_METHODS,
        totalAmount: 0,
        payments: [],
        count: 0
      },
      cash: {
        methods: CASH_METHODS,
        totalAmount: 0,
        payments: [],
        count: 0
      },
      other: {
        methods: [],
        totalAmount: 0,
        payments: [],
        count: 0
      }
    };

    payments.forEach(payment => {
      const method = payment.method || payment.payment_method || payment.paymentMethod || payment.gateway || 'unknown';
      let category = 'other';
      
      if (STRIPE_METHODS.includes(method)) {
        category = 'stripe';
      } else if (CASH_METHODS.includes(method)) {
        category = 'cash';
      }
      
      grouped[category].totalAmount += parseFloat(payment.amount || payment.total_amount || payment.paid_amount) || 0;
      grouped[category].payments.push(payment);
      grouped[category].count++;
    });

    return grouped;
  }

  /**
   * Extrait les IDs de réservation uniques à partir des paiements
   * @param {Array} payments - Liste des paiements
   * @returns {Array} IDs de réservation uniques
   */
  getUniqueReservationIds(payments) {
    const reservationIds = payments
      .map(p => p.reservation_id || (p.reservation && p.reservation.id))
      .filter(Boolean);
    return [...new Set(reservationIds)];
  }

  /**
   * Obtient les réservations avec paiements complétés
   * @param {Array} payments - Liste des paiements
   * @returns {Array} IDs de réservation avec paiements complétés
   */
  getReservationsWithCompletedPayments(payments) {
    const completedPayments = payments.filter(p => SUCCESS_STATUSES.includes(p.status));
    return this.getUniqueReservationIds(completedPayments);
  }

  /**
   * Obtient les réservations avec paiements en attente
   * @param {Array} payments - Liste des paiements
   * @returns {Array} IDs de réservation avec paiements en attente
   */
  getReservationsWithPendingPayments(payments) {
    const pendingPayments = payments.filter(p => PENDING_STATUSES.includes(p.status));
    return this.getUniqueReservationIds(pendingPayments);
  }

  /**
   * Calcule le montant total des paiements
   * @param {Array} payments - Liste des paiements
   * @returns {number} Montant total
   */
  calculateTotalAmount(payments) {
    return payments.reduce((sum, p) => {
      const amount = parseFloat(p.amount || p.total_amount || p.paid_amount) || 0;
      return sum + amount;
    }, 0);
  }

  /**
   * Calcule le montant total des paiements en espèces
   * @param {Array} payments - Liste des paiements
   * @returns {number} Montant total en espèces
   */
  calculateTotalByCashMethod(payments) {
    return payments
      .filter(p => {
        const method = p.method || p.payment_method || p.paymentMethod || p.gateway || 'unknown';
        return CASH_METHODS.includes(method);
      })
      .reduce((sum, p) => sum + (parseFloat(p.amount || p.total_amount || p.paid_amount) || 0), 0);
  }

  /**
   * Calcule le montant total des paiements en ligne
   * @param {Array} payments - Liste des paiements
   * @returns {number} Montant total en ligne
   */
  calculateTotalByOnlineMethod(payments) {
    return payments
      .filter(p => {
        const method = p.method || p.payment_method || p.paymentMethod || p.gateway || 'unknown';
        return STRIPE_METHODS.includes(method);
      })
      .reduce((sum, p) => sum + (parseFloat(p.amount || p.total_amount || p.paid_amount) || 0), 0);
  }

  /**
   * Calcule le montant total par type de paiement
   * @param {Array} payments - Liste des paiements
   * @param {string} type - Type de paiement (rental, commission, etc.)
   * @returns {number} Montant total pour ce type
   */
  calculateTotalByType(payments, type) {
    return payments
      .filter(p => (p.payment_type || p.type) === type)
      .reduce((sum, p) => sum + (parseFloat(p.amount || p.total_amount || p.paid_amount) || 0), 0);
  }

  /**
   * Calcule les totaux par réservation
   * @param {Array} payments - Liste des paiements
   * @returns {Object} Totaux par réservation
   */
  calculateTotalsByReservation(payments) {
    return payments.reduce((acc, payment) => {
      const reservationId = payment.reservation_id || (payment.reservation && payment.reservation.id);
      if (!reservationId) return acc;
      
      if (!acc[reservationId]) {
        acc[reservationId] = {
          total: 0,
          completed: 0,
          pending: 0,
          payments: []
        };
      }
      
      const amount = parseFloat(payment.amount || payment.total_amount || payment.paid_amount) || 0;
      acc[reservationId].total += amount;
      acc[reservationId].payments.push(payment);
      
      if (SUCCESS_STATUSES.includes(payment.status)) {
        acc[reservationId].completed += amount;
      } else if (PENDING_STATUSES.includes(payment.status)) {
        acc[reservationId].pending += amount;
      }
      
      return acc;
    }, {});
  }

  /**
   * Crée une structure de données organisée par réservations avec leurs paiements associés
   * @param {Array} payments - Liste des paiements
   * @returns {Array} Réservations avec leurs paiements associés
   */
  getReservationsWithPayments(payments) {
    const reservationMap = new Map();
    
    // Regrouper les paiements par réservation
    payments.forEach(payment => {
      const reservationId = payment.reservation_id || (payment.reservation && payment.reservation.id);
      if (!reservationId) return;
      
      if (!reservationMap.has(reservationId)) {
        reservationMap.set(reservationId, {
          id: reservationId,
          title: payment.reservation?.title || payment.reservation?.listing?.title || 'Réservation',
          reservation_status: payment.reservation?.reservation_status, // 🔥 AJOUTER LE STATUT DE RÉSERVATION
          start_date: payment.reservation?.start_date,
          end_date: payment.reservation?.end_date,
          payments: [],
          totalAmount: 0,
          completedAmount: 0,
          pendingAmount: 0,
          paymentsByType: {},
          paymentsByStatus: {}
        });
      }
      
      const reservation = reservationMap.get(reservationId);
      const amount = parseFloat(payment.amount || payment.total_amount || payment.paid_amount) || 0;
      const type = payment.payment_type || payment.type || 'unknown';
      const status = payment.status || 'unknown';
      
      reservation.payments.push(payment);
      reservation.totalAmount += amount;
      
      // Compter par statut
      if (SUCCESS_STATUSES.includes(status)) {
        reservation.completedAmount += amount;
      } else if (PENDING_STATUSES.includes(status)) {
        reservation.pendingAmount += amount;
      }
      
      // Compter par type
      if (!reservation.paymentsByType[type]) {
        reservation.paymentsByType[type] = { count: 0, amount: 0 };
      }
      reservation.paymentsByType[type].count++;
      reservation.paymentsByType[type].amount += amount;
      
      // Compter par statut
      if (!reservation.paymentsByStatus[status]) {
        reservation.paymentsByStatus[status] = { count: 0, amount: 0 };
      }
      reservation.paymentsByStatus[status].count++;
      reservation.paymentsByStatus[status].amount += amount;
    });
    
    return Array.from(reservationMap.values());
  }

  /**
   * Récupère les constantes de paiement (maintenant depuis le fichier de constantes)
   */
  getPaymentConstants() {
    return {
      PAYMENT_TYPES,
      PAYMENT_STATUSES,
      PAYMENT_METHODS,
      CURRENCIES,
      STRIPE_METHODS,
      CASH_METHODS,
      SUCCESS_STATUSES,
      PENDING_STATUSES
    };
  }

  /**
   * Récupère le payment de remboursement pour le locataire suite à un dispute résolu en sa faveur
   * @param {string} reservationId - ID de la réservation
   * @returns {Promise<Object>} Payment de remboursement ou null
   */
  async getRenterRefundByReservation(reservationId) {
    try {
      const response = await api.get(
        `/payments/renter-refund/reservation/${reservationId}`
      );

      return response?.data?.data ?? null;
    } catch (error) {
      // Si c'est une erreur 404, retourner null (pas de refund trouvé)
      if (error.response?.status === 404) {
        return null;
      }
      // Pour les autres erreurs, les propager
      throw error;
    }
  }
}

// Instance singleton
const paymentService = new PaymentService();

export { paymentService };
